import {Component, inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog} from "@angular/material/dialog";
import {
  AttachHypothesisFileParams,
  HypothesisFilesColumns,
  HypothesisFilesData,
  HypothesisModalDialogData
} from "./types/hypothesis-modal.types";
import {HypothesisFilesStateService} from "./services/hypothesis-files-state.service";
import {HypothesisFilesService} from "./services/hypothesis-files.service";
import {BaseModalComponent} from "../base-modal/base-modal.component";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {hypothesisModalColumn, hypothesisModalName, hypothesisResourceType} from "./configs/hypothesis-modal-config";
import {RecordData, RecordUpdateRequestParams} from "../../../types/records-table.types";
import {FileCardComponent} from "../../../../shared/components/ui/file-card/file-card.component";
import {PreviewFilesStateService} from "../../../services/preview-files-state.service";
import {PreviewFileUrlsStateService} from "../../../services/preview-file-urls-state.service";
import {FileUploadCardComponent} from "../../../../shared/components/ui/file-upload-card/file-upload-card.component";
import {AttachFileParams, HypothesisFileTypeName} from "../../../types/file.types";
import {ConfirmationStatus} from "../../../types/confirmation-modal.types";
import {HypothesisModalService} from "./services/hypothesis-modal.service";
import {EditorTextModalComponent} from "../editor-text-modal/editor-text-modal.component";
import {LoaderComponent} from "../../../../shared/components/loader/loader.component";
import {MatTooltip} from "@angular/material/tooltip";
import {CdkTextareaAutosize} from "@angular/cdk/text-field";
import {FormsModule} from "@angular/forms";
import {MatMiniFabButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";

@Component({
  selector: 'app-hypothesis-modal',
  standalone: true,
  imports: [
    BaseModalComponent,
    MatFormField,
    MatInput,
    MatLabel,
    FileCardComponent,
    FileUploadCardComponent,
    LoaderComponent,
    MatTooltip,
    CdkTextareaAutosize,
    FormsModule,
    MatMiniFabButton,
    MatIcon
  ],
  templateUrl: './hypothesis-modal.component.html',
  styleUrl: './hypothesis-modal.component.scss'
})
export class HypothesisModalComponent extends BaseModalComponent {
  public hypothesisDialogData = inject<HypothesisModalDialogData>(MAT_DIALOG_DATA);
  public hypothesisFilesStateService: HypothesisFilesStateService = inject(HypothesisFilesStateService);
  public previewFileUrlsStateService: PreviewFileUrlsStateService = inject(PreviewFileUrlsStateService);
  private hypothesisFilesService: HypothesisFilesService = inject(HypothesisFilesService);
  private previewFilesStateService: PreviewFilesStateService = inject(PreviewFilesStateService);
  private hypothesisModalService: HypothesisModalService = inject(HypothesisModalService);
  private dialog: MatDialog = inject(MatDialog);

  public readonly hypothesisModalColumn = hypothesisModalColumn;

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.modalStatesService.openModal(hypothesisModalName);
    this.editModeService.setEditMode(true);
    await this.getFiles();
    this.hypothesisFilesService.getFileUrls();
    await this.hypothesisModalService.setUrlQueryParams(this.hypothesisDialogData.id);
  }

  public override async ngOnDestroy(): Promise<void> {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(hypothesisModalName);
    this.hypothesisFilesStateService.resetHypothesisFilesState();
    this.previewFilesStateService.resetPreviewFilesState();
    this.previewFileUrlsStateService.resetPreviewFileUrlsState();
    await this.hypothesisModalService.resetUrlQueryParams();
  }

  public getTooltip(key: keyof HypothesisModalDialogData): string {
    if (this.isEditModeActive || !this.hypothesisDialogData[key]) {
      return '';
    }

    return String(this.hypothesisDialogData[key]).replace(/\n/g, '\n');
  }

  public async handleValueChange(key: keyof HypothesisModalDialogData): Promise<void> {
    const recordUpdateRequestParams: RecordUpdateRequestParams = {
      path: hypothesisResourceType.path,
      recordId: Number(this.hypothesisDialogData.id),
      fieldName: key,
      value: this.hypothesisDialogData[key]
    };

    await this.updatedRecordField<RecordData>(recordUpdateRequestParams);
  }

  public async handleDelete(isImage: boolean, hypothesisFilesData: HypothesisFilesData): Promise<void> {
    this.hypothesisFilesService.deleteHypothesisFile(hypothesisFilesData.id)
      .then(() => {
        this.hypothesisFilesStateService.deleteHypothesisFilesState(hypothesisFilesData.type, hypothesisFilesData.id);
        if (isImage) {
          this.previewFilesStateService.removeFileById(hypothesisFilesData.file.id);
          this.previewFileUrlsStateService.deletePreviewFileUrlById(hypothesisFilesData.file.id);
        }
      });
  }

  public async attachFile({ parentId, fileId, type }: AttachFileParams): Promise<void> {
    const attachHypothesisFileParams: AttachHypothesisFileParams = {
      hypothesisId: parentId,
      fileId: fileId,
      type: type as HypothesisFileTypeName
    };

    await this.hypothesisFilesService.attachHypothesisFile(attachHypothesisFileParams)
      .then((hypothesisFilesData) => this.hypothesisFilesStateService.updateHypothesisFilesState(type as HypothesisFileTypeName, hypothesisFilesData));
  }

  public async handleOpenFullscreenText(column: HypothesisFilesColumns): Promise<void> {
    const textDialogData = this.hypothesisModalService.setTextDialogData(this.hypothesisDialogData, column);
    this.dialog.open(EditorTextModalComponent, {
      width: '800px',
      data: textDialogData
    }).afterClosed().subscribe((result?: string | number | ConfirmationStatus) => {
      if (result !== undefined && result !== 'cancel') {
        (this.hypothesisDialogData[column.inputName] as string | number) = result;
      }
    });
  }

  private async getFiles(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    await this.hypothesisFilesService.getHypothesisFiles(this.hypothesisDialogData.id)
      .then((hypothesisFilesData) => this.hypothesisFilesStateService.setInitialHypothesisFilesState(hypothesisFilesData))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }
}
